import Image from "../components/image";
import Link from "../components/router-link";
import { ArrowUpRight } from "lucide-react";
import { CountUp, Reveal, Stagger, StaggerItem } from "../components/motion";
import { ImageReveal } from "../components/image-reveal";
import { CTA, SectionHeading } from "../components/site-shell";
import { CaseDisclosureCard } from "../components/case-disclosure-card";
import { HeroCarousel } from "../components/hero-carousel";
import { SolutionComparison } from "../components/solution-comparison";
import { cases, method, services, solutionCards } from "../lib/site-data";
import { createMetadata } from "../lib/seo";

export const metadata = createMetadata({
  title: "企业品牌增长与产品出海",
  description:
    "增长深度 GrowDeep 为制造、外贸、消费与创新企业提供品牌增长、产品出海、官网与独立站建设、内容营销、SEO/GEO 和海外获客服务。",
  path: "/",
  keywords: [
    "企业品牌增长",
    "品牌增长服务",
    "企业品牌建设",
    "产品出海",
    "制造业出海",
    "外贸企业出海",
    "企业官网建设",
    "海外独立站建设",
    "内容增长",
    "SEO优化",
    "GEO优化",
    "AI搜索优化",
    "海外获客",
    "增长深度",
    "GrowDeep",
  ],
});

const partnerNames = [
  "制造企业",
  "消费品牌",
  "创新科技",
  "专业服务",
  "外贸企业",
  "区域龙头",
  "产业品牌",
  "SaaS 与 AI",
];

export default function HomePage() {
  return (
    <>
      <section className="home-hero">
        <HeroCarousel />
        <div className="home-hero-inner">
          <Reveal>
            <div className="hero-kicker">
              Enterprise brand growth & global expansion
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h1>
              <span>企业品牌增长，</span>
              <span>助力产品出海获客</span>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p>
              面向制造企业、外贸企业、消费品牌、创新企业与专业服务机构，
              我们以品牌、网站、内容、搜索、数据与运营，建立企业长期掌握的增长资产。
            </p>
          </Reveal>
          <Reveal delay={0.24} className="hero-actions">
            <Link className="button button-light" href="/contact">
              预约增长诊断 <ArrowUpRight size={17} />
            </Link>
            <Link className="button button-outline" href="/solutions">
              探索解决方案
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="home-intro section-shell section-block">
        <div className="intro-grid">
          <Reveal className="intro-copy">
            <span className="eyebrow">品牌资产，而非一次性项目</span>
            <h2>让每一个页面、每一篇内容和每一条线索，都服务于长期增长。</h2>
            <p>
              我们不把网站看作一次性交付，也不将增长等同于短期投放。从品牌定位和客户需求出发，建设可运营的官网与内容体系，再通过
              SEO、GEO、线索承接与数据反馈，把分散动作连接为完整路径。
            </p>
            <div className="stats-grid">
              <div className="stat">
                <strong>
                  <CountUp end={6} />
                </strong>
                <span>项核心能力</span>
              </div>
              <div className="stat">
                <strong>
                  <CountUp end={5} />
                </strong>
                <span>个增长环节</span>
              </div>
              <div className="stat">
                <strong>
                  <CountUp end={1} suffix="套" />
                </strong>
                <span>套长期增长系统</span>
              </div>
              <div className="stat">
                <strong>
                  <CountUp end={100} suffix="%" />
                </strong>
                <span>关键动作可追踪</span>
              </div>
            </div>
          </Reveal>
          <ImageReveal className="intro-visual" delay={0.1} mode="wipe">
            <Image
              className="visual-main"
              src="/assets/品牌资产，而非一次性项目.png"
              alt="团队在办公空间讨论品牌增长"
              width={900}
              height={700}
            />
            <Image
              className="visual-small"
              src="/assets/team.jpg"
              alt="跨职能团队协作"
              width={700}
              height={480}
            />
            <div className="visual-note">
              <strong>AI 增强效率，专业团队负责判断</strong>
              <span>从策略到上线、复盘，关键节点均有明确记录</span>
            </div>
          </ImageReveal>
        </div>
      </section>

      <section className="home-solutions section-shell section-block-sm">
        <SectionHeading
          eyebrow="Solutions"
          title="两条增长路径，帮助企业建立自己的客户渠道。"
          description="面向国内市场提升品牌认知与稳定获客；面向全球市场建设独立站与海外询盘能力。"
          action={
            <Link className="text-link" href="/solutions">
              查看解决方案 <ArrowUpRight size={16} />
            </Link>
          }
        />
        <Stagger className="solutions-duo">
          <StaggerItem className="solution-feature solution-feature-comparison">
            <SolutionComparison solutions={solutionCards.slice(0, 2)} />
          </StaggerItem>
        </Stagger>
      </section>

      <section className="home-services section-shell section-block-sm">
        <SectionHeading
          eyebrow="Services"
          title="六项核心能力，覆盖品牌增长与产品出海的关键环节。"
          description="从价值表达与网站阵地，到内容、搜索、线索与运营，按企业所处阶段组合实施。"
          action={
            <Link className="text-link" href="/services">
              查看全部服务 <ArrowUpRight size={16} />
            </Link>
          }
        />
        <Stagger className="service-grid">
          {services.map(({ icon: Icon, title, description }) => (
            <StaggerItem className="service-tile" key={title}>
              <Icon />
              <h3>{title}</h3>
              <p>{description}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <section className="home-cases section-muted section-block">
        <div className="section-shell">
          <SectionHeading
            eyebrow="Service scenarios"
            title="从业务问题出发，选择合适的增长路径。"
            description="说明不同企业适用的服务方式、执行周期与可验收交付，不展示未经授权的项目结果。"
            action={
              <Link className="text-link" href="/cases">
                查看全部案例 <ArrowUpRight size={16} />
              </Link>
            }
          />
          <Stagger className="case-grid">
            {cases.slice(0, 3).map((item) => (
              <StaggerItem className="case-disclosure-item" key={item.title}>
                <CaseDisclosureCard item={item} />
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="home-method section-shell section-block-sm">
        <SectionHeading
          eyebrow="Method"
          title="增长不是单点交付，而是可持续运营的方法体系。"
          description="以业务问题为起点，用清晰的目标、动作、反馈和复盘推进每一个增长阶段。"
        />
        <Stagger className="method-grid">
          {method.map(({ icon: Icon, number, title, text }) => (
            <StaggerItem className="method-step" key={number}>
              <span className="method-number">{number}</span>
              <h3>
                <Icon />
                {title}
              </h3>
              <p>{text}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <CTA />
    </>
  );
}
